import { useCreditUsage } from "@/hooks/use-credit-usage";
import { cn } from "@/lib/utils";
import { Zap } from "lucide-react";

function getColor(percentage: number): string {
  if (percentage >= 100) return "bg-red-500";
  if (percentage >= 80) return "bg-orange-500";
  if (percentage >= 60) return "bg-yellow-500";
  return "bg-emerald-500";
}

function FeatureBar({
  label,
  used,
  limit,
}: {
  label: string;
  used: number;
  limit: number;
}) {
  if (limit === -1) return null; // unlimited
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground w-20 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all", getColor(pct))}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-muted-foreground tabular-nums w-16 text-right">
        {used}/{limit}
      </span>
    </div>
  );
}

export function CreditUsageBar() {
  const { data, loading } = useCreditUsage();

  if (loading || !data) return null;

  const hasLimits =
    data.copilot.limit !== -1 ||
    data.agents.limit !== -1 ||
    data.automations.limit !== -1;

  if (!hasLimits) return null;

  return (
    <div className="flex items-start gap-2 px-4 py-2 border-b border-border bg-card/50">
      <Zap className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
      <div className="flex-1 space-y-1.5 min-w-0">
        <FeatureBar
          label="Copilot"
          used={data.copilot.used}
          limit={data.copilot.limit}
        />
        <FeatureBar
          label="Agentes"
          used={data.agents.used}
          limit={data.agents.limit}
        />
        <FeatureBar
          label="Automacoes"
          used={data.automations.used}
          limit={data.automations.limit}
        />
      </div>
    </div>
  );
}
